/**
 * Where a suggestion of an approved page shows, and what an Accept sends. The suggestion's own `text` and the fields it
 * `needs` are Tina's; IMS only decides which box the suggestion sits in and which answer fields it collects.
 */
import type { PageForm } from "@/pages/tina/festivals/lib/page-form";
import type {
  ExistingChoiceKind,
  SuggestionAcceptBody,
  SuggestionRead,
} from "@/types/tina-festival-page";

export type SuggestionPlace =
  | { where: "festival" }
  | { where: "day"; key: string }
  | { where: "new_day" }
  | { where: "existing" }
  | { where: "top" };

/** The box a suggestion sits in: its day's, the festival's, the existing list's, or the top of the page. */
export function placeSuggestion(
  suggestion: SuggestionRead,
  form: PageForm,
): SuggestionPlace {
  switch (suggestion.kind) {
    case "new_name":
      return { where: "festival" };
    case "new_day":
      return { where: "new_day" };
    case "conflict":
      return { where: "existing" };
    case "cancel":
    case "moved_day":
    case "day_unlisted":
    case "lineup":
    case "not_added":
    case "mood_changed":
    case "mood_deleted": {
      if (suggestion.day === null) return { where: "top" };
      const box = form.days.find(
        (d) => d.loadedDay === suggestion.day || d.day === suggestion.day,
      );
      return box ? { where: "day", key: box.key } : { where: "top" };
    }
  }
}

/** What the curator fills in before Accept. */
export interface SuggestionInput {
  artist: { id: string; name: string; photoUrl: string | null } | null;
  venue: { id: string; name: string } | null;
  choice: ExistingChoiceKind | null;
  day: string | null;
  makeCanonical: boolean;
}

/** The input a suggestion starts with: Tina's own pre-fill. */
export function initialInput(suggestion: SuggestionRead): SuggestionInput {
  return {
    artist: null,
    venue:
      suggestion.venue_id === null
        ? null
        : { id: suggestion.venue_id, name: suggestion.venue_name ?? "" },
    choice: suggestion.default_choice,
    day: suggestion.day,
    makeCanonical: false,
  };
}

/** Accept is on once every field the suggestion `needs` is filled; a lineup suggestion's artist is optional. */
export function canAccept(
  suggestion: SuggestionRead,
  input: SuggestionInput,
): boolean {
  return suggestion.needs.every((need) => {
    switch (need) {
      case "artist":
        return suggestion.kind === "lineup" || input.artist !== null;
      case "venue":
        return input.venue !== null;
      case "choice":
        return (
          input.choice !== null &&
          (input.choice === "not_part" || input.day !== null)
        );
    }
  });
}

/** Only the fields `needs` names, plus the name flag of a new name and the artist of a lineup or not-added act. */
export function acceptBody(
  suggestion: SuggestionRead,
  input: SuggestionInput,
): SuggestionAcceptBody {
  const body: SuggestionAcceptBody = {};
  const withArtist =
    suggestion.needs.includes("artist") ||
    suggestion.kind === "lineup" ||
    suggestion.kind === "not_added";
  if (withArtist && input.artist !== null) {
    body.artist_id = input.artist.id;
    if (input.artist.photoUrl !== null) body.photo_url = input.artist.photoUrl;
  }
  if (suggestion.needs.includes("venue") && input.venue !== null) {
    body.venue_id = input.venue.id;
    body.venue_name = input.venue.name;
  }
  if (suggestion.needs.includes("choice") && input.choice !== null) {
    body.choice = input.choice;
    body.day = input.choice === "not_part" ? null : input.day;
  }
  if (suggestion.kind === "new_name") body.make_canonical = input.makeCanonical;
  return body;
}
